/**
 * ObjectiveInbox
 * 
 * Executive view for reviewing Catchball proposal packets.
 * Displays proposals organized by strategic objectives.
 * 
 * Features:
 * - Filter by objective(s)
 * - List proposal packets mapped to each objective
 * - Show: proposer, timestamp, OST tree summary, readiness summary
 * - Three action buttons: Accept with conditions, Return for revision, Reject
 * 
 * Per canonical requirements:
 * - Catchball tracks direction, ownership, status, and artifacts (Doc 04)
 * - No invisible work; artifacts must persist and be reviewable (Doc 01)
 * 
 * Per Doc 06 (Queues & Query Truth Table) and T-RESP-04:
 * - Responsibility classification is server-side canonical truth
 * - This component integrates with get_responsibilities for canonical actions
 * - UI-side filtering or inference is forbidden for canonical queues
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Inbox,
  Target,
  ChevronDown,
  ChevronRight,
  Clock,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RotateCcw,
  User,
  TreeDeciduous,
  FileText,
  Filter,
  RefreshCw,
  Check,
  X,
  MessageSquare,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { CatchballStickyHeader } from '../../../components/prioritization/catchball/CatchballStickyHeader';
import { CallBehindProposal } from '../../../components/prioritization/catchball/CallBehindProposal';
import { CATCHBALL_LABELS } from '../../../lib/catchball/vocabulary';
import { useProposalPacketStore, fetchProposalContext } from '../../../stores/prioritization/proposalPacketStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { computeLoopAccess, computeLoopVisibility } from '../../../hooks/prioritization/useOrgAwareCatchball';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useCatchballVirtualConfigStore } from '../../../stores/prioritization/catchballVirtualConfigStore';
import { useCatchballBallStore } from '../../../stores/prioritization/catchballBallStore';
import { getBallPossession, POSSESSION_LABELS } from '../../../types/prioritization/catchballLoop';
import { getActionLabel, getActionDescription } from '../../../stores/prioritization/nextStepStore';
import { useResponsibilityStore } from '../../../stores/prioritization/responsibilityStore';
import { ResponsibilityCardView } from '../../../components/prioritization/responsibility';
import { buildFallbackCatchballResponsibilities } from '../../../lib/catchballFallbackResponsibilities';
import { useCatchballAcceptRight } from '../../../hooks/prioritization/useActRight';
import { refusedByRight } from '../../../lib/actRight';
import type {
  CatchballProposal,
  StructuredBlockers,
  CatchballThreadEntry,
} from '../../../types/prioritization/catchballProposal';

// ============================================================================
// Types
// ============================================================================

interface ObjectiveInboxProps {
  cycleId: string;
  embedded?: boolean;
}

type StatusFilter = 'all' | 'pending' | 'returned' | 'accepted' | 'rejected';

// ============================================================================
// Ball Holder Badge Component
// ============================================================================

function BallHolderBadge({
  scope,
}: {
  scope: string | null;
  userId?: string | null;
}) {
  if (!scope) return null;
  
  const labels: Record<string, { label: string; color: string }> = {
    executive: { label: 'Executive has ball', color: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
    middle: { label: 'Team has ball', color: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
    bottom: { label: 'Contributor has ball', color: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
    system: { label: 'System', color: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' },
  };
  
  const config = labels[scope] || { label: scope, color: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' };
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 ${config.color} text-xs font-medium rounded-full`}>
      <User className="h-3 w-3" />
      {config.label}
    </span>
  );
}

// ============================================================================
// Thread History Component
// ============================================================================

function ThreadHistory({ 
  entries,
  isLoading,
}: { 
  entries: CatchballThreadEntry[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="text-center py-4">
        <div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full mx-auto" />
        <p className="text-xs text-[var(--text-ter)] mt-1">Loading thread...</p>
      </div>
    );
  }
  
  if (entries.length === 0) {
    return (
      <div className="text-center py-4 text-sm text-[var(--text-ter)]">
        No conversation history yet
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {entries.map((entry, idx) => (
        <div 
          key={entry.id || idx} 
          className={`p-3  ${
            entry.authorRole === 'executive' 
              ? 'bg-[var(--slate-dim)] border-l-4 border-[var(--slate)]' 
              : 'bg-[var(--slate-dim)] border-l-4 border-[var(--slate)]'
          }`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-[var(--text-sec)]">
              {/* The person, when the record names them; their side otherwise. */}
              {entry.authorName ||
                (entry.authorRole
                  ? entry.authorRole.replace(/^./, (c) => c.toUpperCase())
                  : 'Unknown')}
            </span>
            <span className="text-xs text-[var(--text-ter)]">
              {entry.createdAt.toLocaleString()}
            </span>
          </div>
          
          {entry.messageType && (
            <span className={`inline-block text-xs px-1.5 py-0.5 rounded mb-2 ${
              entry.messageType === 'accept_provisional' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
              entry.messageType === 'return' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' :
              entry.messageType === 'reject' ? 'bg-[var(--coral-dim)] text-[var(--coral)]' :
              'bg-[var(--hover-tint)] text-[var(--text-sec)]'
            }`}>
              {entry.messageType === 'return'
                ? 'Returned'
                : entry.messageType === 'accept_provisional'
                  ? 'Accepted provisionally'
                  : entry.messageType.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())}
            </span>
          )}
          
          {entry.body && (
            <p className="text-sm text-[var(--text-sec)]">{entry.body}</p>
          )}
          
          {entry.structuredBlockers && (
            <div className="mt-2 text-xs space-y-1">
              {entry.structuredBlockers.missingCriteriaLock && (
                <div className="flex items-center gap-1 text-[var(--mustard)]">
                  <AlertTriangle className="h-3 w-3" />
                  Criteria must be locked
                </div>
              )}
              {entry.structuredBlockers.questions?.map((q, i) => (
                <div key={i} className="flex items-center gap-1 text-[var(--text-ter)]">
                  <MessageSquare className="h-3 w-3" />
                  {q}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Status Badge Component
// ============================================================================

function ProposalStatusBadge({ status }: { status: CatchballProposal['status'] }) {
  switch (status) {
    case 'pending':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs font-medium rounded-full">
          <Clock className="h-3 w-3" />
          Pending Review
        </span>
      );
    case 'accepted':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-xs font-medium rounded-full">
          <CheckCircle2 className="h-3 w-3" />
          Accepted
        </span>
      );
    case 'accepted_provisional' as CatchballProposal['status']:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-xs font-medium rounded-full">
          <CheckCircle2 className="h-3 w-3" />
          Accepted provisionally
        </span>
      );
    case 'returned' as CatchballProposal['status']:
    case 'countered':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs font-medium rounded-full">
          <RotateCcw className="h-3 w-3" />
          Returned
        </span>
      );
    case 'rejected':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--coral-dim)] text-[var(--coral)] text-xs font-medium rounded-full">
          <XCircle className="h-3 w-3" />
          Rejected
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs font-medium rounded-full">
          {/* A status this screen has no words for is still shown in words. */}
          {String(status).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())}
        </span>
      );
  }
}

// ============================================================================
// Readiness Summary Component
// ============================================================================

function ReadinessSummary({ proposal }: { proposal: CatchballProposal }) {
  const snapshot = proposal.readinessSnapshot;
  if (!snapshot) return null;

  const { summary } = snapshot;
  const totalGaps = summary.itemsWithGaps;
  const hasIssues = totalGaps > 0 || summary.missingCriteriaLock || summary.missingRanges.length > 0;

  return (
    <div className={`text-xs px-2 py-1 rounded ${
      hasIssues ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' : 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
    }`}>
      {hasIssues ? (
        <span className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          {totalGaps} item{totalGaps !== 1 ? 's' : ''} with gaps
          {summary.missingCriteriaLock && ', criteria not locked'}
        </span>
      ) : (
        <span className="flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" />
          All items ready
        </span>
      )}
    </div>
  );
}

// ============================================================================
// Proposal Card Component
// ============================================================================

function ProposalCard({
  proposal,
  onSelect,
  isSelected,
  ballHolderScope,
}: {
  proposal: CatchballProposal;
  onSelect: (id: string) => void;
  isSelected: boolean;
  ballHolderScope?: string | null;
}) {
  const nodeCount = proposal.ostNodeIds?.length || 0;
  const itemCount = Object.keys(proposal.readinessSnapshot?.items || {}).length;

  return (
    <div
      onClick={() => onSelect(proposal.id)}
      className={`p-4 border  cursor-pointer transition-colors ${
        isSelected
          ? 'border-primary-500 bg-primary-50'
          : 'border-[var(--rule)] hover:border-[var(--rule)] hover:bg-[var(--surface)]'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <ProposalStatusBadge status={proposal.status} />
            {ballHolderScope && (
              <BallHolderBadge scope={ballHolderScope} />
            )}
            {proposal.comparabilityState === 'pre_comparable' && (
              <span className="text-xs text-[var(--text-ter)] bg-[var(--hover-tint)] px-1.5 py-0.5 rounded">
                Pre-Arrange
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2 text-sm text-[var(--text-sec)] mt-2">
            <User className="h-3.5 w-3.5" />
            <span>{proposal.proposedByName || 'Unknown'}</span>
            <span className="text-[var(--text-ter)]">·</span>
            <span>{proposal.proposedAt.toLocaleDateString()}</span>
          </div>
          
          {/* Counts are shown only when there is something to count: a
              proposal that carries its answer in words has neither. */}
          {(nodeCount > 0 || itemCount > 0) && (
            <div className="flex items-center gap-4 mt-2 text-xs text-[var(--text-ter)]">
              {nodeCount > 0 && (
                <span className="flex items-center gap-1">
                  <TreeDeciduous className="h-3 w-3" />
                  {nodeCount} OST node{nodeCount !== 1 ? 's' : ''}
                </span>
              )}
              {itemCount > 0 && (
                <span className="flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  {itemCount} item{itemCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}
        </div>
        
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <ReadinessSummary proposal={proposal} />
          <Link
            to={`/prioritization/cascade/proposal/${proposal.id}`}
            className="text-sm font-medium text-primary-600 hover:text-primary-800"
            onClick={(e) => e.stopPropagation()}
          >
            Review proposal
          </Link>
        </div>
      </div>
      
      {proposal.rationale && (
        <p className="mt-3 text-sm text-[var(--text-sec)] line-clamp-2">
          {proposal.rationale}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Proposal Detail Panel
// ============================================================================

function ProposalDetailPanel({
  proposal,
  threadEntries,
  isLoadingThread,
  ballHolderScope,
  nextStep,
  onAccept,
  onReturn,
  onReject,
  isSubmitting,
}: {
  proposal: CatchballProposal;
  threadEntries: CatchballThreadEntry[];
  isLoadingThread: boolean;
  ballHolderScope?: string | null;
  nextStep?: {
    id: string;
    suggestedAction: string;
    suggestedPhase?: string;
    isBlocked: boolean;
    blockers: Array<{ description: string; remediationPath?: string }>;
  } | null;
  onAccept: () => void;
  onReturn: (blockers: StructuredBlockers, message: string) => void;
  onReject: (message: string) => void;
  isSubmitting: boolean;
}) {
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [returnMessage, setReturnMessage] = useState('');
  const [rejectMessage, setRejectMessage] = useState('');
  const [blockers, setBlockers] = useState<StructuredBlockers>({
    missingCriteriaLock: false,
    missingRanges: [],
    missingValues: [],
    altitudeMismatch: [],
  });

  const snapshot = proposal.readinessSnapshot;
  const isPending = proposal.status === 'pending';
  // D-4 (migration 987): Accept is drawn only for a person the database says
  // may accept (the rule catchball_accept acts on, including the cycle's
  // "Items / selection" decision right); otherwise a plain line says why.
  const acceptRight = useCatchballAcceptRight(isPending ? proposal.id : null);

  const handleReturn = () => {
    onReturn(blockers, returnMessage);
    setShowReturnForm(false);
  };

  const handleReject = () => {
    onReject(rejectMessage);
    setShowRejectForm(false);
  };

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[var(--text-pri)]">Proposal Detail</h3>
          <div className="flex items-center gap-2">
            <Link
              to={`/prioritization/cascade/proposal/${proposal.id}`}
              className="text-sm font-medium text-primary-600 hover:text-primary-800"
            >
              Review proposal
            </Link>
            <ProposalStatusBadge status={proposal.status} />
            {ballHolderScope && <BallHolderBadge scope={ballHolderScope} />}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2 text-sm text-[var(--text-sec)]">
          <User className="h-4 w-4" />
          <span>{proposal.proposedByName || 'Unknown'}</span>
          <span className="text-[var(--text-ter)]">·</span>
          <span>{proposal.proposedAt.toLocaleString()}</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Rationale */}
        <div>
          <h4 className="text-sm font-medium text-[var(--text-pri)] mb-2">Rationale</h4>
          <p className="text-sm text-[var(--text-sec)]">
            {proposal.rationale || 'No rationale provided'}
          </p>
        </div>

        {/* OST Nodes */}
        {proposal.ostNodeIds && proposal.ostNodeIds.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-[var(--text-pri)] mb-2">
              OST Nodes ({proposal.ostNodeIds.length})
            </h4>
            <div className="text-xs text-[var(--text-ter)] bg-[var(--surface)] p-2 rounded">
              {proposal.ostNodeIds.join(', ')}
            </div>
          </div>
        )}

        {/* Readiness Snapshot */}
        {snapshot && (
          <div>
            <h4 className="text-sm font-medium text-[var(--text-pri)] mb-2">Readiness Snapshot</h4>
            <div className="bg-[var(--surface)] p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--text-sec)]">Total Items</span>
                <span className="font-medium">{snapshot.summary.totalItems}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-sec)]">Ready</span>
                <span className="font-medium text-[var(--green)] dark:text-[var(--text-ter)]">{snapshot.summary.itemsReady}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-sec)]">With Gaps</span>
                <span className="font-medium text-[var(--mustard)]">{snapshot.summary.itemsWithGaps}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-sec)]">Criteria Locked</span>
                <span className={snapshot.summary.missingCriteriaLock ? 'text-[var(--coral)]' : 'text-[var(--green)] dark:text-[var(--text-ter)]'}>
                  {snapshot.summary.missingCriteriaLock ? 'No' : 'Yes'}
                </span>
              </div>
              {snapshot.summary.missingRanges.length > 0 && (
                <div className="pt-2 border-t border-[var(--rule)]">
                  <span className="text-[var(--coral)]">Missing ranges: {snapshot.summary.missingRanges.join(', ')}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Next Step CTA (shown after acceptance) */}
        {nextStep && (
          <div className={` p-4 ${nextStep.isBlocked ? 'bg-[var(--mustard-dim)] border border-[var(--mustard)]' : 'bg-[var(--green-dim)] border border-[var(--green)]'}`}>
            <h4 className="text-sm font-medium text-[var(--text-pri)] mb-2 flex items-center gap-2">
              {nextStep.isBlocked ? (
                <AlertTriangle className="h-4 w-4 text-[var(--mustard)]" />
              ) : (
                <CheckCircle2 className="h-4 w-4 text-[var(--green)]" />
              )}
              Next Step: {getActionLabel(nextStep.suggestedAction)}
            </h4>
            <p className="text-sm text-[var(--text-sec)] mb-2">
              {getActionDescription(nextStep.suggestedAction, 'CATCHBALL_ACCEPT_PROVISIONAL')}
            </p>
            {nextStep.isBlocked && nextStep.blockers.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-[var(--mustard)]">Blocked by:</p>
                {nextStep.blockers.map((b, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-[var(--mustard)]">
                    <XCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                    <span>{b.description}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Thread History */}
        <div>
          <h4 className="text-sm font-medium text-[var(--text-pri)] mb-2 flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Conversation ({threadEntries.length})
          </h4>
          <ThreadHistory entries={threadEntries} isLoading={isLoadingThread} />
        </div>

        {/* Return Form */}
        {showReturnForm && (
          <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4 space-y-4">
            <h4 className="font-medium text-[var(--text-sec)]">{CATCHBALL_LABELS.returnForRevision}</h4>
            
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={blockers.missingCriteriaLock}
                  onChange={e => setBlockers(b => ({ ...b, missingCriteriaLock: e.target.checked }))}
                  className="rounded border-[var(--rule)]"
                />
                <span className="text-sm">Criteria must be locked</span>
              </label>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Message (optional)
              </label>
              <textarea
                value={returnMessage}
                onChange={e => setReturnMessage(e.target.value)}
                rows={3}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                placeholder="Add context or questions..."
              />
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={handleReturn}
                disabled={isSubmitting}
                className="px-4 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] disabled:opacity-50"
              >
                Confirm return
              </button>
              <button
                onClick={() => setShowReturnForm(false)}
                className="px-4 py-2 text-[var(--text-sec)] text-sm font-medium hover:bg-[var(--hover-tint)]"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Reject Form */}
        {showRejectForm && (
          <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4 space-y-4">
            <h4 className="font-medium text-[var(--coral)]">Reject Proposal</h4>
            
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Reason (required)
              </label>
              <textarea
                value={rejectMessage}
                onChange={e => setRejectMessage(e.target.value)}
                rows={3}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                placeholder="Why is this proposal being rejected?"
              />
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={handleReject}
                disabled={isSubmitting || !rejectMessage.trim()}
                className="px-4 py-2 bg-[var(--coral)] text-white text-sm font-medium hover:bg-[var(--coral)] disabled:opacity-50"
              >
                Confirm Reject
              </button>
              <button
                onClick={() => setShowRejectForm(false)}
                className="px-4 py-2 text-[var(--text-sec)] text-sm font-medium hover:bg-[var(--hover-tint)]"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* The call behind what is about to be accepted: a standing is never drawn alone */}
      {isPending && !showReturnForm && !showRejectForm && (
        <div className="px-6 py-4 border-t border-[var(--rule)]">
          <CallBehindProposal proposal={proposal} />
        </div>
      )}

      {/* Action Buttons */}
      {isPending && !showReturnForm && !showRejectForm && (
        <div className="px-6 py-4 border-t border-[var(--rule)] bg-[var(--surface)] flex flex-wrap gap-3">
          {acceptRight.line && (
            <p className="basis-full text-sm text-[var(--text-ter)]">{acceptRight.line}</p>
          )}
          {acceptRight.allowedProvisional && (
            <button
              onClick={onAccept}
              disabled={isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--green)] text-white font-medium hover:bg-[var(--green)] disabled:opacity-50"
            >
              <Check className="h-4 w-4" />
              {CATCHBALL_LABELS.acceptWithConditions}
            </button>
          )}
          {/* 1001: rejecting and returning exercise the right accepting does — not drawn when it shuts this person out (build contract §2.8) */}
          {!refusedByRight(acceptRight) && (
            <>
              <button
                onClick={() => setShowReturnForm(true)}
                disabled={isSubmitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--slate)] text-white font-medium hover:bg-[var(--slate)] disabled:opacity-50"
              >
                <MessageSquare className="h-4 w-4" />
                {CATCHBALL_LABELS.returnForRevision}
              </button>
              <button
                onClick={() => setShowRejectForm(true)}
                disabled={isSubmitting}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-[var(--coral)] text-[var(--coral)] font-medium hover:bg-[var(--coral-dim)] disabled:opacity-50"
              >
                <X className="h-4 w-4" />
                Reject
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * The packet statuses that are genuinely waiting on the executive. `returned`
 * is back with the proposer, and the accepted ones are done.
 */
const AWAITING_EXECUTIVE_STATUSES = new Set(['proposed', 'countered', 'pending']);

export default function ObjectiveInbox({ cycleId, embedded = false }: ObjectiveInboxProps) {
  // State
  const [selectedProposalId, setSelectedProposalId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [expandedObjectives, setExpandedObjectives] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [threadEntries, setThreadEntries] = useState<CatchballThreadEntry[]>([]);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [proposalContext, setProposalContext] = useState<{
    whoHasBall?: { scope: string | null; userId: string | null } | null;
    nextSteps?: Array<{
      id: string;
      sourceEvent: string;
      suggestedAction: string;
      suggestedPhase: string | null;
      blockedReasonsSnapshot: Record<string, unknown> | null;
      createdAt: string;
      resolvedAt: string | null;
    }>;
  } | null>(null);

  // Stores
  const proposals = useProposalPacketStore(state => state.proposals);
  const fetchProposals = useProposalPacketStore(state => state.fetchProposals);
  const fetchThreadForProposal = useProposalPacketStore(state => state.fetchThreadForProposal);
  const acceptProvisional = useProposalPacketStore(state => state.acceptProvisional);
  const returnWithBlockers = useProposalPacketStore(state => state.returnWithBlockers);
  const rejectProposal = useProposalPacketStore(state => state.rejectProposal);
  const getObjectivesWithProposals = useProposalPacketStore(state => state.getObjectivesWithProposals);
  const isLoading = useProposalPacketStore(state => state.isLoading);
  const userId = useUserStore(state => state.userId);
  const cycle = useDegapProcessStore(state => state.cycles.find(c => c.id === cycleId));
  
  // Loop store for ball holder and "awaiting executive response" logic
  const allLoops = useCatchballLoopStore(state => state.loops);
  const fetchLoops = useCatchballLoopStore(state => state.fetchFromSupabase);
  const cycleLoops = useMemo(
    () => allLoops.filter(l => l.cycleId === cycleId).sort((a, b) => a.loopNumber - b.loopNumber),
    [allLoops, cycleId]
  );
  const orgUnits = useEnterpriseIdentityStore(state => state.orgUnits);
  const userNodeIndex = useEnterpriseIdentityStore(state => state.userNodeIndex);
  const fetchEnterpriseIdentity = useEnterpriseIdentityStore(state => state.fetchFromSupabase);
  const fetchVirtualConfig = useCatchballVirtualConfigStore(state => state.fetchForCycle);
  const virtualConfig = useCatchballVirtualConfigStore(state => cycleId ? (state.configs[cycleId] ?? null) : null);
  const allBallsById = useCatchballBallStore(state => state.ballsById);
  const fetchBallsForCycle = useCatchballBallStore(state => state.fetchBallsForCycle);
  const visibleLoops = useMemo(
    () => cycleLoops.filter(loop => {
      const ballsForLoop = Object.values(allBallsById).filter(b => b.loopId === loop.id);
      return computeLoopVisibility(loop, userId ?? null, orgUnits, userNodeIndex, cycle?.orgScopeNodeId, virtualConfig, ballsForLoop, cycle?.ownerId ?? null).canSeeLoop;
    }),
    [cycleLoops, userId, orgUnits, userNodeIndex, cycle?.orgScopeNodeId, cycle?.ownerId, virtualConfig, allBallsById]
  );
  const visibleLoopIds = useMemo(() => new Set(visibleLoops.map(l => l.id)), [visibleLoops]);
  // Proposal packets awaiting executive response: loop has proposal and ball is with executive (visibility-filtered)
  const proposalsAwaitingResponse = useMemo(() => {
    const list: { loop: import('../../../types/prioritization/catchballLoop').CatchballLoop; packet: CatchballProposal }[] = [];
    for (const loop of visibleLoops) {
      const ballsForLoop = Object.values(allBallsById).filter(b => b.loopId === loop.id);
      const canActOnLoop = computeLoopAccess(loop, userId ?? null, orgUnits, userNodeIndex, cycle?.orgScopeNodeId, virtualConfig, ballsForLoop, cycle?.ownerId ?? null).canActOnLoop;
      if (!loop.proposalId || !canActOnLoop) continue;
      // A loop accumulates packets — a returned one and its revision both sit
      // against the same loop. `find` took whichever came back first, so the
      // panel could show a superseded packet. The loop names its current one.
      const onThisLoop = proposals.filter(p => p.id === loop.proposalId || (p.proposalType === 'packet' && p.loopId === loop.id));
      const packet =
        onThisLoop.find(p => p.id === loop.proposalId) ??
        [...onThisLoop].sort((a, b) =>
          new Date(b.proposedAt ?? 0).getTime() - new Date(a.proposedAt ?? 0).getTime())[0];
      // "Awaiting my response" means exactly that. This used to list any packet
      // on the loop regardless of status, so an accepted or already-returned
      // packet still appeared as something the executive owed an answer to.
      if (packet && AWAITING_EXECUTIVE_STATUSES.has(String(packet.status))) list.push({ loop, packet });
    }
    return list;
  }, [visibleLoops, proposals, userId, orgUnits, userNodeIndex, cycle?.orgScopeNodeId, cycle?.ownerId, virtualConfig, allBallsById]);
  
  // Next step store (reserved for proposal CTA use)
  
  // Canonical responsibility store (per Doc 06 / T-RESP-04)
  const responsibilities = useResponsibilityStore(state => state.responsibilities);
  const fetchResponsibilities = useResponsibilityStore(state => state.fetchResponsibilities);
  const refreshResponsibilities = useResponsibilityStore(state => state.refreshResponsibilities);
  
  // Filter responsibilities relevant to this inbox (catchball scope)
  const inboxResponsibilities = useMemo(() =>
    responsibilities.filter(r =>
      r.scope === 'CATCHBALL' &&
      r.classification === 'REQUIRED' &&
      (
        r.deep_link?.includes(cycleId) ||
        r.valid_actions.some(action => action.route.includes(cycleId))
      )
    ),
    [responsibilities, cycleId]
  );
  const fallbackResponsibilities = useMemo(
    () =>
      buildFallbackCatchballResponsibilities({
        loops: visibleLoops,
        cycles: cycle ? [cycle] : [],
        currentUserId: userId ?? null,
        orgUnits,
        userNodeIndex,
        existingResponsibilities: inboxResponsibilities,
        cycleId,
      }),
    [visibleLoops, cycle, userId, orgUnits, userNodeIndex, inboxResponsibilities, cycleId]
  );
  const displayedResponsibilities = inboxResponsibilities.length > 0 ? inboxResponsibilities : fallbackResponsibilities;
  
  // Handler for navigation errors per T-RESP-07
  const handleResponsibilityNavigationError = useCallback((cardId: string, error: string) => {
    console.error(`[ObjectiveInbox] Navigation error for ${cardId}:`, error);
    if (userId) {
      refreshResponsibilities(userId);
    }
  }, [userId, refreshResponsibilities]);

  // Load proposals and loops
  useEffect(() => {
    fetchProposals(cycleId);
    fetchLoops(cycleId);
    void fetchEnterpriseIdentity();
    void fetchVirtualConfig(cycleId);
    void fetchBallsForCycle(cycleId);
    if (userId) {
      fetchResponsibilities(userId);
    }
  }, [cycleId, fetchEnterpriseIdentity, fetchProposals, fetchLoops, fetchVirtualConfig, fetchBallsForCycle, userId, fetchResponsibilities]);
  
  // Load thread and context when proposal is selected
  useEffect(() => {
    if (!selectedProposalId) {
      setThreadEntries([]);
      setProposalContext(null);
      return;
    }
    
    const loadContext = async () => {
      setIsLoadingThread(true);
      try {
        // Fetch thread entries
        const entries = await fetchThreadForProposal(selectedProposalId);
        setThreadEntries(entries);
        
        // Fetch full context via RPC
        const context = await fetchProposalContext(selectedProposalId);
        if (context) {
          setProposalContext({
            whoHasBall: context.whoHasBall,
            nextSteps: context.nextSteps,
          });
        }
      } catch (err) {
        console.error('[ObjectiveInbox] Failed to load context:', err);
      } finally {
        setIsLoadingThread(false);
      }
    };
    
    loadContext();
  }, [selectedProposalId, fetchThreadForProposal]);
  
  // Get ball holder for a proposal's loop
  const getBallHolderForProposal = useCallback((proposal: CatchballProposal) => {
    if (!proposal.loopId) return null;
    const loop = allLoops.find(l => l.id === proposal.loopId);
    return loop?.ballHolderScope ?? (loop ? getBallPossession(loop.state) : null);
  }, [allLoops]);

  // Filter proposals (visibility: only proposals whose loop is in scope)
  const filteredProposals = useMemo(() => {
    return proposals.filter(p => {
      if (p.cycleId !== cycleId) return false;
      if (p.proposalType !== 'packet') return false;
      if (p.loopId && !visibleLoopIds.has(p.loopId)) return false;
      if (statusFilter === 'all') return true;
      if (statusFilter === 'pending') return p.status === 'pending';
      if (statusFilter === 'returned') return p.status === 'countered';
      if (statusFilter === 'accepted') return p.status === 'accepted';
      if (statusFilter === 'rejected') return p.status === 'rejected';
      return true;
    });
  }, [proposals, cycleId, statusFilter, visibleLoopIds]);

  // Group by objective
  const objectivesWithProposals = useMemo(() => {
    return getObjectivesWithProposals(cycleId);
  }, [cycleId, getObjectivesWithProposals]);

  // Selected proposal
  const selectedProposal = useMemo(() => {
    return proposals.find(p => p.id === selectedProposalId);
  }, [proposals, selectedProposalId]);

  // Handlers
  const handleRefresh = useCallback(() => {
    fetchProposals(cycleId);
    fetchLoops(cycleId);
    void fetchEnterpriseIdentity();
    void fetchVirtualConfig(cycleId);
    void fetchBallsForCycle(cycleId);
    if (userId) {
      fetchResponsibilities(userId);
    }
  }, [cycleId, fetchEnterpriseIdentity, fetchLoops, fetchProposals, fetchVirtualConfig, fetchBallsForCycle, fetchResponsibilities, userId]);

  const toggleObjective = useCallback((objId: string) => {
    setExpandedObjectives(prev => {
      const next = new Set(prev);
      if (next.has(objId)) {
        next.delete(objId);
      } else {
        next.add(objId);
      }
      return next;
    });
  }, []);

  const handleAccept = useCallback(async () => {
    if (!selectedProposalId || !userId) return;
    
    setIsSubmitting(true);
    try {
      await acceptProvisional({
        proposalId: selectedProposalId,
        responseType: 'accept_provisional',
        actorId: userId,
      });
      await fetchProposals(cycleId);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedProposalId, userId, acceptProvisional, fetchProposals, cycleId]);

  const handleReturn = useCallback(async (blockers: StructuredBlockers, message: string) => {
    if (!selectedProposalId || !userId) return;
    
    setIsSubmitting(true);
    try {
      await returnWithBlockers({
        proposalId: selectedProposalId,
        responseType: 'return',
        actorId: userId,
        structuredBlockers: blockers,
        message,
      });
      await fetchProposals(cycleId);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedProposalId, userId, returnWithBlockers, fetchProposals, cycleId]);

  const handleReject = useCallback(async (message: string) => {
    if (!selectedProposalId || !userId) return;
    
    setIsSubmitting(true);
    try {
      await rejectProposal({
        proposalId: selectedProposalId,
        responseType: 'reject',
        actorId: userId,
        message,
      });
      await fetchProposals(cycleId);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedProposalId, userId, rejectProposal, fetchProposals, cycleId]);

  const packetStatusLabel = (status: string | undefined) => {
    switch (status) {
      case 'pending': return 'Awaiting executive response';
      case 'accepted': return 'Accepted';
      case 'rejected': return 'Rejected';
      default: if (status && /revision|returned/i.test(String(status))) return 'Returned for revision'; return status ?? 'Awaiting executive response';
    }
  };

  return (
    <div className={embedded ? 'flex flex-col' : 'min-h-full bg-[var(--surface)] dark:bg-[var(--bg)] flex flex-col'}>
      {!embedded && <CatchballStickyHeader cycleId={cycleId} active="inbox" />}

      {/* Header */}
      <div className="px-6 py-4 border-b border-[var(--rule)] bg-[var(--bg-raised)]">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="p-2 bg-primary-100">
              <Inbox className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-[var(--text-pri)]">Proposal Review</h1>
              <p className="text-sm text-[var(--text-ter)]">
                {cycle?.name || 'Cycle'} · Proposal packets awaiting your response
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-[var(--text-ter)]" />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as StatusFilter)}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] border border-[var(--rule)] px-3 py-1.5 text-sm"
              >
                <option value="all">All Proposals</option>
                <option value="pending">Pending</option>
                <option value="returned">Returned</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            
            {/* Refresh */}
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 text-[var(--text-ter)] hover:bg-[var(--hover-tint)]"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {cycleLoops.length > 0 && visibleLoops.length === 0 && (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] px-6 py-4 text-sm text-[var(--mustard)]">
            No loops are within your org scope. Contact your administrator if you believe this is incorrect.
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Proposal List */}
        <div className="w-1/2 border-r border-[var(--rule)] overflow-y-auto bg-[var(--surface)]">
          <div className="p-4 space-y-4">
            {/* Proposal packets awaiting executive response */}
            <div>
              <h2 className="text-sm font-semibold text-[var(--text-pri)] mb-2">Proposal packets awaiting my response</h2>
              {proposalsAwaitingResponse.length === 0 ? (
                <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-6 text-center">
                  <Inbox className="h-10 w-10 text-[var(--text-ter)] mx-auto mb-2" />
                  <p className="text-sm text-[var(--text-sec)]">No proposals awaiting response.</p>
                </div>
              ) : (
                <div className="space-y-3 mb-4">
                  {proposalsAwaitingResponse.map(({ loop, packet }) => {
                    const possession = getBallPossession(loop.state);
                    return (
                      <div
                        key={loop.id}
                        onClick={() => setSelectedProposalId(packet.id)}
                        className={` border p-4 cursor-pointer transition-colors ${
                          selectedProposalId === packet.id ? 'border-primary-500 bg-primary-50' : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)]'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-[var(--text-pri)]">Loop {loop.loopNumber}</div>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)]">
                                {packetStatusLabel(packet.status)}
                              </span>
                              {packet.proposedAt && (
                                <span className="text-xs text-[var(--text-ter)]">
                                  Submitted {new Date(packet.proposedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                              )}
                            </div>
                            <div className="mt-1.5 text-xs text-[var(--text-sec)]">
                              {POSSESSION_LABELS[possession]} has the ball
                            </div>
                          </div>
                          <Link
                            to={`/prioritization/cascade/proposal/${packet.id}`}
                            className="shrink-0 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-800"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Review proposal
                            <ChevronRight className="h-4 w-4" />
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Your Required Actions - canonical from server (per Doc 06 / T-RESP-04) */}
            {displayedResponsibilities.length > 0 && (
              <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
                <h3 className="text-sm font-semibold text-[var(--text-sec)] mb-3 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Ball holder queue
                </h3>
                <div className="space-y-2">
                  {displayedResponsibilities.map(resp => (
                    <ResponsibilityCardView
                      key={resp.id}
                      card={resp}
                      onNavigationError={handleResponsibilityNavigationError}
                      compact
                    />
                  ))}
                </div>
                {/* Both halves used to describe the implementation to the reader
                    — "the canonical Catchball queue", and a fallback "derived
                    from the current loop state … while responsibility sync
                    catches up". Which internal path produced the list is not
                    something the person holding the work needs to know; what it
                    contains is. */}
                <p className="text-xs text-[var(--text-sec)] mt-2">
                  {inboxResponsibilities.length > 0
                    ? 'Work that is in your hands right now.'
                    : 'Work that is in your hands right now, read from the loops themselves.'}
                </p>
              </div>
            )}

            <h3 className="text-sm font-semibold text-[var(--text-sec)] pt-2">All proposals</h3>
            
            {isLoading && filteredProposals.length === 0 ? (
              <div className="text-center py-8">
                <div className="animate-spin h-8 w-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto" />
                <p className="text-sm text-[var(--text-ter)] mt-2">Loading proposals...</p>
              </div>
            ) : filteredProposals.length === 0 ? (
              <div className="text-center py-8">
                <Inbox className="h-12 w-12 text-[var(--text-ter)] mx-auto" />
                <p className="text-[var(--text-ter)] mt-2">No proposals to review</p>
              </div>
            ) : (
              objectivesWithProposals.length > 0 ? (
                objectivesWithProposals.map(obj => (
                  <div key={obj.objectiveId} className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
                    {/* Objective Header */}
                    <div
                      onClick={() => toggleObjective(obj.objectiveId)}
                      className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[var(--surface)]"
                    >
                      <div className="flex items-center gap-2">
                        {expandedObjectives.has(obj.objectiveId) ? (
                          <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />
                        )}
                        <Target className="h-4 w-4 text-[var(--text-ter)]" />
                        <span className="font-medium text-[var(--text-pri)]">{obj.objectiveName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {obj.pendingCount > 0 && (
                          <span className="px-2 py-0.5 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs font-medium rounded-full">
                            {obj.pendingCount} pending
                          </span>
                        )}
                        <span className="text-sm text-[var(--text-ter)]">{obj.proposals.length} total</span>
                      </div>
                    </div>
                    
                    {/* Proposals */}
                    {expandedObjectives.has(obj.objectiveId) && (
                      <div className="border-t border-[var(--rule)] p-3 space-y-2">
                        {filteredProposals
                          .filter(p => p.objectiveIds?.includes(obj.objectiveId))
                          .map(proposal => (
                            <ProposalCard
                              key={proposal.id}
                              proposal={proposal}
                              onSelect={setSelectedProposalId}
                              isSelected={proposal.id === selectedProposalId}
                              ballHolderScope={getBallHolderForProposal(proposal)}
                            />
                          ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                // Flat list if no objective grouping
                <div className="space-y-3">
                  {filteredProposals.map(proposal => (
                    <ProposalCard
                      key={proposal.id}
                      proposal={proposal}
                      onSelect={setSelectedProposalId}
                      isSelected={proposal.id === selectedProposalId}
                      ballHolderScope={getBallHolderForProposal(proposal)}
                    />
                  ))}
                </div>
              )
            )}
          </div>
        </div>

        {/* Detail Panel */}
        <div className="w-1/2 overflow-hidden">
          {selectedProposal ? (
            <ProposalDetailPanel
              proposal={selectedProposal}
              threadEntries={threadEntries}
              isLoadingThread={isLoadingThread}
              ballHolderScope={proposalContext?.whoHasBall?.scope}
              nextStep={proposalContext?.nextSteps?.[0] ? {
                id: proposalContext.nextSteps[0].id,
                suggestedAction: proposalContext.nextSteps[0].suggestedAction,
                suggestedPhase: proposalContext.nextSteps[0].suggestedPhase || undefined,
                isBlocked: false, // Will be computed at render time
                blockers: [],
              } : null}
              onAccept={handleAccept}
              onReturn={handleReturn}
              onReject={handleReject}
              isSubmitting={isSubmitting}
            />
          ) : (
            <div className="h-full flex items-center justify-center bg-[var(--surface)]">
              <div className="text-center">
                <FileText className="h-12 w-12 text-[var(--text-ter)] mx-auto" />
                <p className="text-[var(--text-ter)] mt-2">Select a proposal to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
